import { checkArrayNotEmpty } from '@cuemath/intel';
import {
  BULK_TRANSFER_REASONS_OPTIONS,
  EXCEPTION_REASONS,
  TRANSFER_REASONS,
  STUDENT_TRANSFER_REASONS_OPTIONS,
  stCreatedByDummyId,
} from '../constants/student-transfer';
import { DAYS_LIST, DAYS_SHORT_FORM, NUM_TO_MONTH_SHORT } from '../constants/date';
import { getCookie } from 'utils';

const initDaySlotsValue = {
  Monday: 0,
  Tuesday: 0,
  Wednesday: 0,
  Thursday: 0,
  Friday: 0,
  Saturday: 0,
  Sunday: 0,
};

const getNextOccurrenceTimestamp = (day, time) => {
  if (!day || !time) return null;

  const targetDay = DAYS_LIST.indexOf(day) + 1;

  //* Create a Date object for the current date and time
  const currentDate = new Date();
  const currentDay = currentDate.getDay();

  let daysUntilTargetDay = targetDay - currentDay;

  if (daysUntilTargetDay <= 0) {
    //* Calculate days until next occurrence of the target day
    daysUntilTargetDay += 7;
  }

  //* Create a Date object for the target date and time
  let nextOccurrenceDate = new Date();
  nextOccurrenceDate.setDate(currentDate.getDate() + daysUntilTargetDay);

  const [hours, minutes] = time.split(':');
  nextOccurrenceDate.setHours(hours);
  nextOccurrenceDate.setMinutes(minutes);
  nextOccurrenceDate.setSeconds(0);
  nextOccurrenceDate.setMilliseconds(0);

  const finalDate = nextOccurrenceDate.getTime() / 1000;

  return finalDate;
};

const convertTransferReasonForPatchData = transferReason => {
  let transferReasonForPatchData = '';

  STUDENT_TRANSFER_REASONS_OPTIONS.forEach(reasonOption => {
    const { id, label } = reasonOption || {};

    if (label === transferReason || id === transferReason) {
      transferReasonForPatchData = id;

      return;
    }
  });

  if (!transferReasonForPatchData) {
    BULK_TRANSFER_REASONS_OPTIONS.forEach(reasonOption => {
      const { id, label } = reasonOption || {};

      if (label === transferReason) {
        transferReasonForPatchData = id;

        return;
      }
    });
  }

  return transferReasonForPatchData;
};

export const getSingleStudentTransferPostData = ({
  transferReason,
  slotsList,
  preferredTeacherData,
}) => {
  const userId = getCookie('user_id') || getCookie('emp_id');

  const finalPostData = {
    created_by_id: userId || stCreatedByDummyId,
  };

  const logSchedules =
    checkArrayNotEmpty(slotsList) &&
    slotsList.filter(slotInfo => !slotInfo.day || !slotInfo.time).length === 0;

  if (transferReason) {
    // Admin direct transfer (ADMIN_DIRECT_TRANSFER) isn't a user-selectable
    // reason option, so the converter returns '' for it. The assign endpoint
    // requires a reason and validates it against a choices list, so an empty
    // string fails (" is not a valid choice"). Fall back to ADMIN_DIRECT_TRANSFER
    // so the two-phase admin assign carries a valid reason.
    finalPostData.reason =
      convertTransferReasonForPatchData(transferReason) || 'ADMIN_DIRECT_TRANSFER';
  }

  if (logSchedules) {
    const schedules = slotsList.reduce((acc, slotInfo) => {
      const { day, time } = slotInfo || {};

      return {
        ...acc,
        [DAYS_LIST.indexOf(day) + 1]: getNextOccurrenceTimestamp(day, time),
      };
    }, {});

    finalPostData.schedules = schedules;
  }

  if (preferredTeacherData?.preferredTeacherId) {
    finalPostData.preferred_teacher_id = preferredTeacherData.preferredTeacherId;
    // Admin two-phase transfer has no course selector; fall back to MATH so we
    // never send course_stream: undefined.
    finalPostData.course_stream = preferredTeacherData.courseSelected || 'MATH';
  }

  return finalPostData;
};

export const singleStudentTransferButtonDisable = ({
  transferReason,
  slotsList,
  preferredTeacherData,
}) => {
  switch (transferReason) {
    case TRANSFER_REASONS['CLASS_SLOT_ISSUE']: {
      const daysSelectedCount = slotsList?.reduce((acc, slotInfo) => {
        const { day } = slotInfo || {};

        return {
          ...acc,
          [day]: acc[day] + 1,
        };
      }, initDaySlotsValue);

      const noSlotTimeProvided = slotsList?.some(
        slotInfo => !slotInfo.time || !slotInfo.day,
      );
      const sameDaySelectedMultipleTimes = Object.values(daysSelectedCount || {}).some(
        count => count >= 2,
      );

      return noSlotTimeProvided || sameDaySelectedMultipleTimes;
    }

    case TRANSFER_REASONS['PREFERRED_TEACHER']: {
      return (
        !preferredTeacherData.courseSelected || !preferredTeacherData.preferredTeacherId
      );
    }

    default:
      return false;
  }
};

export const reasonBasedWarningMessage = selectedTrasferReason => {
  const willGetException = Object.values(EXCEPTION_REASONS).find(
    transferReason => transferReason === selectedTrasferReason,
  );

  if (willGetException) {
    return `Teacher will get an exception for this transfer`;
  }

  return `Teacher will not get an exception for this transfer`;
};

export const getLicenseDetailsText = ({
  licensedGeos,
  licensedGrades,
  specialization,
}) => {
  let licenseDetailsText = '';

  if (checkArrayNotEmpty(licensedGrades)) {
    const lastGrade = licensedGrades?.length - 1;

    (licensedGrades || []).forEach((grades, index) => {
      if (lastGrade === index) {
        licenseDetailsText += grades;
      } else {
        licenseDetailsText += `${grades}, `;
      }
    });
  }

  if (checkArrayNotEmpty(licensedGeos)) {
    const lastGeo = licensedGeos?.length - 1;

    licenseDetailsText += ' • ';

    (licensedGeos || []).forEach((geos, index) => {
      if (lastGeo === index) {
        licenseDetailsText += geos;
      } else {
        licenseDetailsText += `${geos}, `;
      }
    });
  }

  if (checkArrayNotEmpty(specialization)) {
    const lastSpecialization = specialization?.length - 1;

    licenseDetailsText += ' • ';

    (specialization || []).forEach((specializations, index) => {
      if (lastSpecialization === index) {
        licenseDetailsText += specializations;
      } else {
        licenseDetailsText += `${specializations}, `;
      }
    });
  }

  return licenseDetailsText;
};

export const semanticDropdownDataFormat = options => {
  const temp = options?.map(item => {
    const { label, value, id, key } = item;

    return { text: label, key: key || id, value };
  });

  return temp;
};

export const getFilteredStudentTransferInfo = ({ studentList, filterOptions }) => {
  if (!studentList?.length) return [];

  const {
    reasonFilter = [],
    statusFilter = [],
    slotsFilter = [],
    classCompletedFilter = [],
    searchKey = '',
  } = filterOptions || {};

  let filteredStudentList = [...(studentList || [])];

  if (reasonFilter?.length) {
    filteredStudentList = filteredStudentList.filter(studentData => {
      return reasonFilter.includes(TRANSFER_REASONS[studentData?.reason]);
    });
  }

  if (statusFilter?.length) {
    filteredStudentList = filteredStudentList.filter(studentData =>
      statusFilter.includes(studentData.status),
    );
  }

  if (searchKey?.length) {
    filteredStudentList = filteredStudentList.filter(studentData => {
      return (
        studentData?.student_course?.name
          ?.toLowerCase()
          ?.includes(searchKey?.toLowerCase()) ||
        studentData?.student_course?.student_name
          ?.toLowerCase()
          ?.includes(searchKey?.toLowerCase()) ||
        studentData?.student_id?.toLowerCase()?.includes(searchKey?.toLowerCase())
      );
    });
  }

  if (slotsFilter?.length) {
    filteredStudentList = filteredStudentList.filter(studentData => {
      if (slotsFilter.length === 2) {
        return true;
      }

      return slotsFilter[0] === 'Pending'
        ? !studentData.slot_details
        : studentData.slot_details;
    });
  }

  if (classCompletedFilter?.length) {
    filteredStudentList = filteredStudentList.filter(studentData =>
      classCompletedFilter.includes(studentData.class_completed_count),
    );
  }

  return filteredStudentList;
};

const getSlotTime = (slotTime, appendZero) => {
  if (!slotTime) return '';

  const [hours, minutes] = slotTime?.split(':');
  const _24H_time = hours > 12;

  if (appendZero) {
    const _HRS_ = _24H_time ? hours - 12 : hours;
    const _0_HRS_ = _HRS_ <= 9 ? `0${_HRS_}` : _HRS_;
    const _0_MINS = minutes <= 9 ? `0${minutes}` : minutes;

    return `${_0_HRS_}:${_0_MINS} ${_24H_time ? 'PM' : 'AM'}`;
  }

  return `${_24H_time ? hours - 12 : hours}:${minutes} ${_24H_time ? 'PM' : 'AM'}`;
};

export const convertSlotsDataToText = regularSlots => {
  let text = '';
  const slotsDataValues = Object.values(regularSlots || {});

  slotsDataValues.forEach((slotData, index) => {
    const { day: slotDay, time: slotTime } = slotData || {};
    const dayInNumber = DAYS_LIST.indexOf(slotDay) || 0;
    const day = DAYS_SHORT_FORM[dayInNumber];
    const time = getSlotTime(slotTime);

    const lastItem = slotsDataValues.length - 1 === index;

    if (slotsDataValues.length === 1 || lastItem) {
      text += `${day} ${time}`;
    } else {
      text += `${day} ${time} • `;
    }
  });

  return text;
};

export const convertEpochDateToSTSlotTime = regularSlots => {
  let text = '';

  const slotData = Object.entries(regularSlots || {});

  slotData.forEach(([slotDay, slotTime], index) => {
    const day = DAYS_SHORT_FORM[slotDay - 1];
    const newDate = new Date(slotTime * 1000);

    const _HRS_ = newDate.getHours();
    const _MINS_ = newDate.getMinutes();
    const time = getSlotTime(`${_HRS_}:${_MINS_}`, true);

    const lastItem = slotData.length - 1 === index;

    if (slotData.length === 1 || lastItem) {
      text += `${day} ${time}`;
    } else {
      text += `${day} ${time} • `;
    }
  });

  return text;
};

export const getDateOfRequest = createdOn => {
  if (!createdOn) return null;

  const epochToDateObj = new Date(createdOn * 1000);
  const monthNum = epochToDateObj.getMonth() + 1;

  const date = epochToDateObj.getDate();
  const month = NUM_TO_MONTH_SHORT[monthNum];
  const year = epochToDateObj.getFullYear();

  return `${date} ${month} ${year}`;
};

export const getSlotSelectorAddedList = listData => {
  if (!Array.isArray(listData) || !listData.length) return [];

  const updatedListData = listData.map(data => ({ ...data, selected: false }));

  return updatedListData;
};
